import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {select, Store} from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { filter, first, tap } from 'rxjs/operators';


import {Observable, Subscription} from 'rxjs';

import { FollowupOrderOption } from './treatment-next-steps/treatment-next-steps.component';
import {Location} from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ORCADENT_PRODUCT_KEYS, ProductInfoForKey, ProductKey, PROFESSIONAL_PRODUCT_KEYS } from 'libs/product/src/lib/models/product.model';
import { ComplaintsMailToService } from './complaints-mail-to.service';
import { SapService } from 'libs/sap/src/lib/services/sap.service';
import { TranslateService } from '@ngx-translate/core';
import { MessagesService } from 'libs/messages/src/lib/services/messages.service';
import { WebviewerPreviewComponent } from 'libs/ux-components/src/lib/components/webviewer-preview/webviewer-preview.component';
import { FollowupPackageType, DataTransferMethod, InterimScanOption, OrcadentTreatmentPackages, TreatmentModel, TreatmentPackages, TreatmentStatus, TreatmentTypes, Webviewer, WebviewerVisibilityScope } from 'libs/treatments/src/lib/models/treatment.model';
import { User } from 'libs/tm-common/src/lib/models/user.model';
import { TreatmentsService } from 'libs/treatments/src/lib/services/treatments.service';
import { TreatmentState } from 'libs/treatments/src/lib/store/treatment.reducer';
import { NotificationService } from 'libs/tm-common/src/lib/services/notification.service';
import { selectTreatmentFromUrl } from 'libs/treatments/src/lib/store/treatment.selectors';
import { selectIdFromUrl } from 'libs/tm-common/src/lib/store/selectors/selectors';
import { InitiateSubsequentOrder, OrderNextPackage, RejectRefinementRequest, ReleaseRefinementRequest, RequestRefinement, SubmitComplaint, SubmitComplaintSuccess, TreatmentActionTypes, TreatmentSelected } from 'libs/treatments/src/lib/store/treatment.actions';
import { OrcadentPackagesService } from 'libs/treatments/src/lib/services/orcadent-packages.service';
import { ClearAnamneseUploadedFilesAction } from 'libs/anamnese/src/lib/store/anamnese.actions';
import { selectUser } from 'libs/auth/src/lib/store/auth.selectors';
import { MediaService } from 'libs/media/src/lib/services/media.service';
import { ClearMedias, LoadMedias } from 'libs/media/src/lib/store/media.actions';
import { TreatmentMessagesComponent } from 'libs/ux-components/src/lib/container/treatment-messages/treatment-messages.component';
import { TreatmentMediaComponent } from 'libs/ux-components/src/lib/container/treatment-media/treatment-media.component';
import { UpsPickupContainerComponent } from 'libs/ux-components/src/lib/container/ups-pickup-container/ups-pickup-container.component';
import { ConfirmDialogComponent } from 'libs/ux-components/src/lib/components/confirm-dialog/confirm-dialog.component';
import { ProfessionalPackagesService } from 'libs/treatments/src/lib/services/professional-packages.service';
import { QuestionnaireOrcadentDialogComponent } from 'libs/questionaire/src/lib/orcadent/component/questionnaire-orcadent-dialog.component';
import { AnamnesisService } from 'libs/anamnese/src/lib/services/anamnesis.service';
import { ProductInfoService } from 'libs/product/src/lib';
import { GoAction } from 'libs/tm-common/src/lib/store/actions/actions';

/**
 * TODO: Check for which states the input should be disabled
 * In these status, customer inputs, i.e. file uploads and messaging, should be disabled.
 * These are specified by process management.
 */
const INPUT_DISABLED_STATUS_LIST: TreatmentStatus[] = [
	TreatmentStatus.InProduction,
	TreatmentStatus.Planning,
	TreatmentStatus.CanceledByCustomer,
	TreatmentStatus.ConsultationComplete,
	TreatmentStatus.ComplaintResolved,
	TreatmentStatus.Closed,
	TreatmentStatus.Shipped,
];

@Component({
    selector: 'tm2-treatments',
    templateUrl: './treatments.component.html',
    styleUrls: ['./_treatments.component.scss']
})

export class TreatmentsComponent implements OnInit, OnDestroy {
    treatment$: Observable<TreatmentModel>;
    user$: Observable<User>;

    treatment: TreatmentModel;
    orderablePackageType: FollowupPackageType;

	showComplaintButton: boolean = false;
	showUpsButton: boolean = false;

    showPackages: boolean;
    showPackagesOrcadent: boolean;
    showSpecialState: boolean;
    hideOrderNextPackageButton: boolean;

    TreatmentStatus = TreatmentStatus;
    TreatmentTypes = TreatmentTypes;
	selectedTreatmentIdSubscription: Subscription;
	treatmentSubscription: Subscription;
	fileEventSubscription: Subscription;

	isDownloadingCostEstimatePdf: boolean = false;

	protected showOrderMemotainButton = false;

	constructor(
		private readonly dialog: MatDialog,
		private readonly treatmentService: TreatmentsService,
		private readonly mediaService: MediaService,
		private readonly sapService: SapService,
		private readonly location: Location,
		private readonly store: Store<TreatmentState>,
		private readonly actions$: Actions,
		private readonly activeRoute: ActivatedRoute,
		private readonly complaintsMailToService: ComplaintsMailToService,
		private readonly notificationService: NotificationService,
		private readonly translationService: TranslateService,
		private readonly messageService: MessagesService,
		private readonly productInfoService: ProductInfoService,
		private anamnesisService: AnamnesisService
	) { }

    ngOnInit() {
		this.treatment$ = this.store.pipe(
            select(selectTreatmentFromUrl),
            filter((t: TreatmentModel) => !!t)
        );

		this.activeRoute.data.subscribe(data =>
			this.store.dispatch(new LoadMedias({medias: data.medias}))
		);

		this.user$ = this.store.pipe(select(selectUser));

		 // save treatmentId from url as selected treatment in store
		this.selectedTreatmentIdSubscription =  this.store.pipe(select(selectIdFromUrl))
		  	.subscribe( treatmentId => this.store.dispatch(new TreatmentSelected(treatmentId)) );


		this.treatmentSubscription = this.treatment$.pipe(
			tap(t => console.log('Changed treatment', t)),
			// set treatment
			tap((t: TreatmentModel) => this.treatment = structuredClone(t)),
			// prepare visibility flags
			tap(t => this.showPackages = this.determinePackageVisibility(t)),
			tap(t => this.showPackagesOrcadent = this.determinePackageVisibilityOrcadent(t)),
			tap(t => this.showSpecialState =
				(t.type === TreatmentTypes.TmAnamnese && t.status === TreatmentStatus.NewRequestReceived) ||
				(t.type === TreatmentTypes.TmAnamnese && t.status === TreatmentStatus.NewImprintsRequested) ||
				(t.type === TreatmentTypes.TmAnamnese && t.status === TreatmentStatus.DataReceived) ||
				(t.type === TreatmentTypes.TmAnamnese && t.status === TreatmentStatus.CorrectionRequestReceived) ||
				(t.type === TreatmentTypes.TmAnamnese && t.status === TreatmentStatus.CanceledByCustomer) ||
				(t.type !== TreatmentTypes.TmAnamnese && t.status === TreatmentStatus.Shipped)
			),
			tap(t => this.hideOrderNextPackageButton = !(t.type === TreatmentTypes.TmAnamnese
				&& (t.status === TreatmentStatus.Shipped || t.status === TreatmentStatus.ComplaintResolved))),
			// complaint button
			tap(t => this.showComplaintButton =
				(t.status === TreatmentStatus.Shipped || t.status === TreatmentStatus.ComplaintResolved)
			),
			//ups button
			tap(t => this.showUpsButton =
				t.product?.options?.dataTransfer === DataTransferMethod.UPS
				&& t.status === TreatmentStatus.NewImprintsRequested
				&& !this.treatmentService.hasPendingUpsPickupRequest(t)
			),

			// determine product state
			tap((t: TreatmentModel) =>
				this.orderablePackageType = t.schema_version >= 2 ? this.treatmentService.determineOrderablePackageType(t) : null
			),

			// order Memotain button
			tap((t: TreatmentModel) => {
				if (t.schema_version >= 2 && this.isProfessionalTreatment()) {
					this.productInfoService.isUserAllowedForProduct(ProductKey.MEMOTAIN_DISCOUNTED)
						.subscribe((isAllowed: boolean) => this.showOrderMemotainButton = isAllowed);
				} else {
					this.showOrderMemotainButton = false;
				}
			})
		).subscribe();

		this.fileEventSubscription = this.sapService.fileEvent.subscribe(
			(fileDownloadSuccess: boolean) => {
				this.isDownloadingCostEstimatePdf = false;
				if (!fileDownloadSuccess) {
					this.notificationService.showErrorMessage(
						this.translationService.instant("CostEstimateDownloadError"),
						null,
						5000
					);
				}
			}
		);
    }

	determinePackageVisibility(treatment: TreatmentModel): boolean {
		if (treatment.type !== TreatmentTypes.TmAnamnese ){
			return false;
		}
		if (!treatment.packages){
			return false;
		}
		if ( treatment.status === TreatmentStatus.NewRequestReceived ||
			treatment.status === TreatmentStatus.CorrectionRequestReceived ){
				return false;
			}
		if ( treatment.status === TreatmentStatus.DataReceived ||
			treatment.status === TreatmentStatus.Planning ){
				return (treatment.packages as TreatmentPackages).next !== 0; //only if not ordered a single package yet
		}
		//if not ordered a single package yet, only show on ReleaseRequested with packages
		if ((treatment.packages as TreatmentPackages).next === 0) {
			return treatment.status === TreatmentStatus.ReleaseRequested && (treatment.packages as TreatmentPackages).steps.length > 0;
		}
		return true;
	}

	determinePackageVisibilityOrcadent(treatment: TreatmentModel): boolean {
		if (!this.isOrcadentTreatment()){
			return false;
		}
		if (!treatment.packages){
			return false;
		}
		if ( treatment.status === TreatmentStatus.NewRequestReceived ||
			 treatment.status === TreatmentStatus.CorrectionRequestReceived ){
			return false;
		}
		if ( treatment.status === TreatmentStatus.DataReceived ||
			 treatment.status === TreatmentStatus.Planning ){
			  return this.isOrcadentTreatmentRunning(); //only if not ordered a single package yet
	    }
		// if not ordered a single package yet, only show on ReleaseRequested
		if ( !this.isOrcadentTreatmentRunning()){
			return treatment.status === TreatmentStatus.ReleaseRequested;
		}
		return true;
	}

    ngOnDestroy() {
		this.selectedTreatmentIdSubscription.unsubscribe();
		this.treatmentSubscription.unsubscribe();
		this.fileEventSubscription.unsubscribe();
		this.showUpsButton = false;
		this.store.dispatch(new ClearMedias());
    }

	onAction(action: { option: FollowupOrderOption, uploadedMediaIds?: string[], comment?: string }) {
		if (action.option === FollowupOrderOption.NEXT_PACKAGE) {
			this.handleNextWithoutImprintAction();
		} else if (action.option === FollowupOrderOption.NEXT_PACKAGE_WITH_IMPRINT_DATA) {
			this.handleNextWithImprintAction(action.uploadedMediaIds);
		} else if (action.option === FollowupOrderOption.REFINEMENT) {
			this.requestRefinement(action.uploadedMediaIds, action.comment);
		}
	}

	private handleNextWithoutImprintAction() {
		if (this.orderablePackageType && this.orderablePackageType !== FollowupPackageType.Package) {
			throw (new Error('got next in action follower order, but no package opportunity'));
		}

		this.hideOrderNextPackageButton = true;
		this.store.dispatch(new OrderNextPackage({ treatmentId: this.treatment._id, packageType: FollowupPackageType.Package, interimScanOption: InterimScanOption.WITHOUT_DATA }));
	}

	private handleNextWithImprintAction(uploadedMediaIds?: string[]) {
		if (this.orderablePackageType && (this.orderablePackageType !== FollowupPackageType.Package && this.orderablePackageType !== FollowupPackageType.FreeCorrection)) {
			throw (new Error('next follower order, but neither package nor free correction opportunity'));
		}

		this.hideOrderNextPackageButton = true;
		const orderPackageType = (this.orderablePackageType && this.orderablePackageType === FollowupPackageType.Package) ? FollowupPackageType.Package : FollowupPackageType.FreeCorrection;
		this.store.dispatch(new OrderNextPackage({ treatmentId: this.treatment._id, packageType: orderPackageType, interimScanOption: InterimScanOption.WITH_DATA }));
		this.updateMediaFiles(uploadedMediaIds);
	}

	private requestRefinement(uploadedMediaIds?: string[], comment?: string) {
		this.hideOrderNextPackageButton = true;
		this.store.dispatch(new RequestRefinement({ treatmentId: this.treatment._id, comment: comment}));
		this.updateMediaFiles(uploadedMediaIds);
	}

	updateMediaFiles(uploadedMediaIds: string[]) {
		if (uploadedMediaIds?.length === 0){
			return;
		}

		const requestBody = {
			treatment: this.treatment._id,
			expiresAt: null
		}

		// update uploaded files with treatmentId and reset 'expiresAt' date
		uploadedMediaIds.forEach(mediaId => {
			if ( mediaId ){
				this.mediaService.updateMedium(mediaId, requestBody);
			}
		});

		// Clear anamnese.uploadedFiles from redux store.
		this.store.dispatch( new ClearAnamneseUploadedFilesAction() );
	}

    onCostEstimate() {
		this.isDownloadingCostEstimatePdf = true;
		const docNum = this.treatment.quotation.docNum;
		this.sapService.getCostEstimatePdf(this.treatment._id, docNum);
    }

	onNotificationsButtonClicked() {
		this.showDialog(TreatmentMessagesComponent, { isInputEnabled: this.isMessageInputEnabled(this.treatment) });
	}

	onDataExchangeButtonClicked() {
		this.showDialog(TreatmentMediaComponent, { isInputEnabled: this.isInputEnabled(this.treatment) });
	}

	onUpsButtonClicked() {
		this.showDialog(UpsPickupContainerComponent, { treatmentId: this.treatment._id, customerWmsId: this.treatment.customerWmsId });
	}

	onWebViewersButtonClicked() {
		this.showDialog(WebviewerPreviewComponent, { webviewers: this.getTreatmentWebviewers() });
	}

	onOrderMemotainClicked(){
		this.store.dispatch(new InitiateSubsequentOrder({
			productKey: ProductKey.MEMOTAIN_DISCOUNTED,
			treatmentId: this.treatment._id,
			patientId: this.treatment.patient._id,
		}))
	}

	onReleaseRefinementButtonClicked() {
		this.store.dispatch(new ReleaseRefinementRequest({treatmentId: this.treatment._id}));
	}

	onRejectRefinementButtonClicked() {
		this.store.dispatch(new RejectRefinementRequest({treatmentId: this.treatment._id}));
	}

	private showDialog(component, data: any = {}) {
		this.dialog.open(component, { width: '75vw', maxHeight: '95vh', data });
	}

	getAlignerProductName() : string
	{
		return ProductInfoForKey[this.treatment.product?.key as ProductKey]?.name;
	}

	onBackButtonClicked(){
		this.location.back();
	}

	onComplaintButtonClicked() {
		const dialogData = {
			title: "ComplaintConfirmDialogTitle",
			msg: "ComplaintConfirmDialogMessage",
			no: "No",
			yes: "Yes",
		};
		const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: dialogData });
		dialogRef.afterClosed().pipe(filter(result => (result === "yes"))).subscribe(() => {
			const oldStatus = this.treatment.status;
			this.actions$.pipe(
				ofType<SubmitComplaintSuccess>(TreatmentActionTypes.SubmitComplaintSuccess),
				first()
			).subscribe(({ payload }) => {
				// XXX Complaints could be submitted for legacy treatments w/o quotation.
				const orderRefNumber = (payload.treatment.schema_version >= 2)
					? payload.treatment.quotation.docNum
					: payload.treatment.orders.at(-1).realId;
				this.complaintsMailToService.openComplaintMail(
					orderRefNumber,
					payload.treatment.patient.patientId
				);
				// Add status change message to event log
				const newStatus = TreatmentStatus.ComplaintReceived;
				this.messageService.createStatusChangeLogMessage(oldStatus, newStatus, this.treatment._id);
			});
			this.store.dispatch(new SubmitComplaint({ treatment: this.treatment }));
		});
	}

	protected onGoToTreatmentClicked(treatmentId: string) {
		this.store.dispatch(new GoAction({ path: [`/treatments/edit/${treatmentId}`] }))
	}

	openOrcadentQuestionnaireDialog(){
		this.anamnesisService.getOrcadentQuestionnaireData(this.treatment._id)
			.pipe(first())
			.subscribe({
				next: (questionnaireData) => {
					const initialWidth = '95%';
					const initialHeight = '95%';
					this.dialog.open(QuestionnaireOrcadentDialogComponent,
						{data: {
							questionnaireData: questionnaireData,
							width: initialWidth,
							height: initialHeight
						},
						width: initialWidth, height: initialHeight});
				},
				error: (error) => {
					console.error("Error fetching treatment plan data", error);
				}
		});
	}

	private isInputEnabled(treatment: TreatmentModel): boolean{
		return !(INPUT_DISABLED_STATUS_LIST.includes(treatment.status as TreatmentStatus));
	}

	private isMessageInputEnabled(treatment: TreatmentModel): boolean {
		return this.isInputEnabled(treatment)
			&& treatment.status !== TreatmentStatus.ComplaintReceived; // XXX Complaints are handled entirely within CRM workflows.
	}

	protected isOrderMemotainButtonEnabled() : boolean {
		return ProfessionalPackagesService.areRegularPackagesConsumed(this.treatment.packages as TreatmentPackages)
			&& this.treatment.status !== TreatmentStatus.RefinementRequestReceived
			&& this.treatment.status !== TreatmentStatus.RefinementReleaseRequested
			&& (this.treatment.packages as TreatmentPackages)?.plannedRefinementAligners === undefined;
	}

	protected getTreatmentWebviewers(): Webviewer[] {
		return this.getPubliclyVisibleWebviewers().concat(this.getLegacyUntrackedWebviewers());
	}

	private getPubliclyVisibleWebviewers(): Webviewer[] {
		return this.treatment.webviewers?.filter((webviewer) => webviewer.visibilityScope ? webviewer.visibilityScope === WebviewerVisibilityScope.PUBLIC : true);
	}

	private getLegacyUntrackedWebviewers(): Webviewer[] {
		const webViewerUrl = this.treatment.quotation?.anamnese?.releaseData?.release?.webViewerUrl;
		const isWebViewerUrlTracked = this.treatment.webviewers?.some(wv => wv.url === webViewerUrl);
		const isCaViewerLink = webViewerUrl?.split('/').includes('ca-viewer'); // As opposed to a legacy OnyxCeph link (webviewer.ca-digit.com).
		return (webViewerUrl && !isWebViewerUrlTracked && isCaViewerLink)
			? [{ name: "Webviewer", url: webViewerUrl, visibilityScope: WebviewerVisibilityScope.PUBLIC }]
			: [];
	}

	protected isAlignerTreatment(treatment: TreatmentModel) {
		return treatment.type === TreatmentTypes.TmAnamnese;
	}

	private isProfessionalTreatment() {
		return PROFESSIONAL_PRODUCT_KEYS.includes(this.treatment.product.key);
	}

	protected isOrcadentTreatment(){
		return ORCADENT_PRODUCT_KEYS.includes(this.treatment.product.key);
	}

	protected isOrcadentTreatmentRunning(){
		return OrcadentPackagesService.isTreatmentRunning(this.treatment.packages as OrcadentTreatmentPackages);
	}

	protected getOrcadentProductName(){
		return ProductInfoForKey[this.treatment.product.key]?.name;
	}

	protected getAlignerPackages() : TreatmentPackages{
		return this.treatment.packages as TreatmentPackages;
	}

	protected getOrcadentPackages() : OrcadentTreatmentPackages{
		return this.treatment.packages as OrcadentTreatmentPackages;
	}

	protected isCostEstimateAvailable() {
		return this.treatment
			&& this.treatment.status === TreatmentStatus.ReleaseRequested
			&& ((this.treatment.packages as TreatmentPackages)?.next === 0 || this.treatment.upgrade?.packages.next === 0);
	}
}
